import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { PricingCards } from "@/components/landing/PricingCards";
import { FAQ } from "@/components/landing/FAQ";
import { SEOHead } from "@/components/seo/SEOHead";
import aiveliqLogo from "@/assets/aiveliq360-logo.png";

export default function Pricing() {
  return (
    <>
      <SEOHead 
        title="Aiveliq360 Pricing | Simple Plans for Scaling Revenue Teams"
        description="Choose the Aiveliq360 plan that fits your growth stage—Starter, Growth, or Enterprise. Transparent pricing for a fully autonomous AI Sales Operating System."
        canonicalUrl="https://aiveliq360.com/pricing"
      />
      <div className="min-h-screen bg-background">
        {/* Header */}
        <nav className="border-b border-border py-3">
          <div className="max-w-6xl mx-auto px-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src={aiveliqLogo} alt="Aiveliq360" className="h-6 object-contain" />
            </Link>
            <div className="flex items-center gap-3">
              <Link to="/">
                <Button variant="ghost" size="sm" className="gap-1.5">
                  <ArrowLeft className="h-3.5 w-3.5" />
                  Back to Home
                </Button>
              </Link>
              <Link to="/auth">
                <Button size="sm">Sign In</Button>
              </Link>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Simple, Transparent Pricing for Every Stage of Growth
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Choose the Aiveliq360 plan that fits your growth stage. Transparent pricing for a fully autonomous AI Sales Operating System.
            </p>
          </div>
        </section>

        {/* Pricing Section */}
        <PricingCards />

        {/* FAQ Section */}
        <FAQ />

        {/* Final CTA */}
        <section className="py-16 px-4 bg-muted/30">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-6">
              Upgrade Your Sales Team With a 360° AI Revenue Engine
            </h2>
            <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
              Join revenue teams that are closing more deals with AI-powered automation, coaching, and intelligence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/auth">
                <Button size="lg" className="w-full sm:w-auto">Start Free Trial</Button>
              </Link>
              <Button size="lg" variant="outline" className="w-full sm:w-auto">Book a Demo</Button>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t border-border py-8">
          <div className="max-w-6xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-4">
            <img src={aiveliqLogo} alt="Aiveliq360" className="h-6 object-contain" />
            <nav className="flex items-center gap-4 text-xs text-muted-foreground">
              <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-foreground transition-colors">Terms</Link>
              <Link to="/cookies" className="hover:text-foreground transition-colors">Cookies</Link>
            </nav>
            <div className="flex flex-col items-center sm:items-end gap-1">
              <p className="text-xs text-muted-foreground">
                © 2025 Aiveliq360. All rights reserved.
              </p>
              <p className="text-xs text-muted-foreground">
                Powered by Stratlance
              </p>
            </div>
          </div>
        </footer>

        {/* FAQ Schema */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What is Aiveliq360?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Aiveliq360 is a 360° AI Sales Operating System that unifies lead generation, sales engagement, forecasting, coaching, and workflow automation into one autonomous platform."
              }
            },
            {
              "@type": "Question",
              "name": "How much does Aiveliq360 cost?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Aiveliq360 offers three plans: Starter at $99/user/month, Growth at $249/user/month, and Enterprise at $2000+/month with custom features."
              }
            },
            {
              "@type": "Question",
              "name": "Is there a free trial?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes, Aiveliq360 offers a free trial so you can experience the full power of the AI Sales Operating System before committing."
              }
            }
          ]
        }) }} />
      </div>
    </>
  );
}
